import { useSessionUser } from '@/contexts/SessionUserContext';
import UnauthCard from '@/layouts/UnauthCard';
import { useVerifyEmailAuthenticationFlow } from '@/queries/authentication';
import Button from 'antd/es/button';
import Typography from 'antd/es/typography';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { styled } from 'styled-components';

const Container = styled.div`
  ${({ theme }) => `
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${theme.paddingLG}px;
`}
`;

const ContentContainer = styled.div`
  ${({ theme }) => `
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.margin}px;
  text-align: center;
`}
`;

const LoadingContainer = styled.div`
  ${({ theme }) => `
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.marginLG}px;
  height: ${theme.sizeXXL * 2}px;
`}
`;

const Spinner = styled.div`
  ${({ theme }) => `
  border: ${theme.lineWidthBold * 2}px solid ${theme.colorBorderBg};
  border-top: ${theme.lineWidthBold * 2}px solid ${theme.colorPrimary};
  border-radius: 50%;
  width: ${theme.sizeXXL * 1.2}px;
  height: ${theme.sizeXXL * 1.2}px;
  animation: spin 2s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`}
`;

export const MagicLinkPage = () => {
  const { userId } = useSessionUser();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const verifyMutation = useVerifyEmailAuthenticationFlow();
  const hasStartedVerification = useRef(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    // Guard against double execution in StrictMode
    if (!hasStartedVerification.current && token) {
      hasStartedVerification.current = true;
      
      verifyMutation.mutate(
        { token },
        {
          onError: () => {
            setError(true);
          },
        }
      );
    } else if (!token && !hasStartedVerification.current) {
      hasStartedVerification.current = true;
      setError(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - we only want this to run once on mount

  // Redirect when user becomes authenticated (after successful verification)
  useEffect(() => {
    if (userId) {
      void navigate('/', { replace: true });
    }
  }, [userId, navigate]);

  if (error) {
    return (
      <Container>
        <UnauthCard>
          <ContentContainer>
            <Typography.Title level={3}>Invalid Link</Typography.Title>
            <Typography.Text>
              This login link is invalid or has expired. Please return to the
              signin page to continue.
            </Typography.Text>
            <Link to="/auth/login">
              <Button type="primary">Back to Sign In</Button>
            </Link>
          </ContentContainer>
        </UnauthCard>
      </Container>
    );
  }

  return (
    <Container>
      <UnauthCard>
        <LoadingContainer>
          <Spinner />
          <Typography.Text>Verifying your login...</Typography.Text>
        </LoadingContainer>
      </UnauthCard>
    </Container>
  );
};

export default MagicLinkPage;
